import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import AppFooter from "../AppFooter";
import { FaHome, FaPlus, FaTh, FaList } from "react-icons/fa";
import SearchFilterBar from "../shared/SearchFilterBar";
import LoadingState from "../common/LoadingState";
import ErrorState from "../common/ErrorState";

// make relative paths absolute using your API base
const toAbs = (baseUrl, p) =>
  !p ? p : /^https?:\/\//i.test(p) ? p : `${baseUrl}${p.startsWith("/") ? "" : "/"}${p}`;

const isImage = (f) =>
  f?.mimetype?.startsWith("image/") ||
  /\.(png|jpe?g|gif|webp|svg)$/i.test(f?.path || f?.url || f || "");

const isVideo = (f) =>
  f?.mimetype?.startsWith("video/") ||
  /\.(mp4|mov|webm|ogg)$/i.test(f?.path || f?.url || f || "");

const pickMediaFromEvent = (evt) => {
  // try common server shapes you use in other places
  const arr = evt?.media || evt?.files || evt?.fileList || [];
  const primary = Array.isArray(arr) ? arr[0] : arr;
  if (primary) return primary;

  return (
    evt?.cover ||
    evt?.image ||
    evt?.image_url ||
    evt?.thumbnail ||
    evt?.banner ||
    null
  );
};

/**
 * Safely parse dates coming from MySQL/Node.
 * - Handles ISO strings
 * - Handles common "YYYY-MM-DD HH:mm:ss" by normalizing to "YYYY-MM-DDTHH:mm:ss"
 * Returns: Date | null
 */
const safeParseDate = (value) => {
  if (!value) return null;

  // Already a Date?
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  // Try native parse first
  const d1 = new Date(value);
  if (!Number.isNaN(d1.getTime())) return d1;

  // Normalize "YYYY-MM-DD HH:mm:ss" -> "YYYY-MM-DDTHH:mm:ss"
  if (typeof value === "string" && value.includes(" ")) {
    const normalized = value.replace(" ", "T");
    const d2 = new Date(normalized);
    if (!Number.isNaN(d2.getTime())) return d2;
  }

  return null;
};

// Hide the tenant default "Timeline" event from list UIs
const isTimelineEvent = (evt) => {
  const name = String(evt?.name ?? evt?.title ?? "").trim().toLowerCase();
  const type = String(evt?.type ?? evt?.eventtype ?? "").trim().toLowerCase();
  return name === "timeline" || type === "timeline";
};

/**
 * Business rule:
 * 1) If an event's end date is <= (current date + 1 day), DO NOT display it
 * 2) If an event's end date is >  (current date + 1 day), DO display it
 *
 * Also uses "status" via event.active (schema: t_event.active).
 * - If event.active is missing (older backend payload), we assume active to avoid breaking existing behavior.
 */
const shouldDisplayEventInList = (evt, cutoffDate) => {
  // ✅ Always hide Timeline
  if (isTimelineEvent(evt)) return false;

  // "status" / active flag handling (supports 1/0, true/false, "1"/"0")
  const hasActiveField = evt?.active !== undefined && evt?.active !== null;
  const isActive =
    !hasActiveField ||
    evt.active === true ||
    evt.active === 1 ||
    evt.active === "1";

  if (!isActive) return false;

  const end = safeParseDate(evt?.enddate);

  // If enddate is missing/invalid, keep it visible (safer fallback; avoids hiding everything)
  if (!end) return true;

  // Rule 1: enddate <= now+1 day => hide
  return end > cutoffDate;
};

export default function EventList() {
  const { tenant } = useParams();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [ownershipFilter, setOwnershipFilter] = useState("all"); // 'all' | 'owned' | 'shared'
  // Media view mode: 'single' = one card per row (current), 'grid' = three across
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('os_eventlist_view') || 'single'; } catch { return 'single'; }
  });
  const setViewModeAndPersist = (mode) => {
    setViewMode(mode);
    try { localStorage.setItem('os_eventlist_view', mode); } catch {}
  };

  const baseUrl = process.env.REACT_APP_BASE_URL || "http://localhost:5000";

  useEffect(() => {
    document.body.classList.add("eventlist-fullbleed");
    return () => document.body.classList.remove("eventlist-fullbleed");
  }, []);

  useEffect(() => {
    if (!tenant) {
      console.error("Tenant ID is missing!");
      setError("Tenant ID is missing.");
      setLoading(false);
      return;
    }

    (async () => {
      try {
        const url = `${baseUrl}/api/events/${tenant}/events`;
        const res = await axios.get(url);

        if (res?.data?.success) {
          const list = Array.isArray(res.data.data) ? res.data.data : [];
          setEvents(list);
        } else {
          throw new Error(res?.data?.message || "Failed to fetch events.");
        }
      } catch (err) {
        console.error("Error fetching events:", err);
        setError(err?.response?.data?.message || "Failed to fetch events.");
      } finally {
        setLoading(false);
      }
    })();
  }, [tenant, baseUrl]);

  const handleCancel = () => navigate(`/${tenant}/dashboard`);

  // Compute cutoff once per render: "current date + 1 day"
  // (Uses local time on the device, which matches the UX expectation in a PWA)
  const cutoffDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d;
  }, []);

  // Filter events for display using "status" (active) + enddate rules
  const visibleEvents = useMemo(() => {
    if (!Array.isArray(events) || events.length === 0) return [];
    let result = events.filter((evt) => shouldDisplayEventInList(evt, cutoffDate));

    // Ownership filter (all / owned / shared)
    if (ownershipFilter === 'owned') {
      result = result.filter((evt) => evt.ownership !== 'shared');
    } else if (ownershipFilter === 'shared') {
      result = result.filter((evt) => evt.ownership === 'shared');
    }

    const q = searchTerm.trim().toLowerCase();
    if (q) {
      result = result.filter((evt) => {
        const name = (evt?.name || "").toLowerCase();
        const desc = (evt?.description || "").toLowerCase();
        const loc = (evt?.location || "").toLowerCase();
        return name.includes(q) || desc.includes(q) || loc.includes(q);
      });
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter((evt) => {
        const start = safeParseDate(evt?.startdate);
        return start ? start >= from : true;
      });
    }
    if (dateTo) {
      const to = new Date(dateTo + "T23:59:59");
      result = result.filter((evt) => {
        const start = safeParseDate(evt?.startdate);
        return start ? start <= to : true;
      });
    }
    // Sort by start date descending
    result.sort((a, b) => {
      const da = safeParseDate(a?.startdate) || new Date(0);
      const db = safeParseDate(b?.startdate) || new Date(0);
      return db - da;
    });
    return result;
  }, [events, cutoffDate, ownershipFilter, searchTerm, dateFrom, dateTo]);

  // The error branch previously used an inline style={{ color: "#f77" }} — a
  // light red set with no background to guarantee it against, so it depended
  // on the theme's page colour to stay readable. Both branches now use the
  // shared states.
  if (loading)
    return (
      <div className="feed-page">
        <div className="feed">
          <LoadingState messageKey="events.loading" />
        </div>
      </div>
    );

  if (error)
    return (
      <div className="feed-page">
        <div className="feed">
          <ErrorState message={String(error)} />
        </div>
      </div>
    );

  // NOTE: empty-state used to early-return without the AppFooter / filter tabs,
  // which left the "Shared with Me" empty view stranded. We now render the
  // empty message inline so the user keeps the footer + ability to switch tabs.

  return (
    <div className={`feed-page feed-page--${viewMode}`}>
      <div className={`feed feed--${viewMode}`}>
        <SearchFilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onClear={() => { setSearchTerm(""); setDateFrom(""); setDateTo(""); setOwnershipFilter("all"); }}
          searchPlaceholder={"Search events\u2026"}
        />

        {/* Ownership filter tabs */}
        <div className="flex gap-2 px-2 pb-2" style={{ gridColumn: "1 / -1" }}>
          {[
            { key: "all", label: t("events.filterAll", "All") },
            { key: "owned", label: t("events.filterOwned", "My Events") },
            { key: "shared", label: t("events.filterShared", "Shared with Me") },
          ].map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setOwnershipFilter(tab.key)}
              className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                ownershipFilter === tab.key
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white dark:bg-neutral-800 text-neutral-600 dark:text-neutral-300 border-neutral-300 dark:border-neutral-600"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Desktop inline action bar (replaces footer on desktop) */}
        <div className="desktop-action-bar" style={{ gridColumn: "1 / -1" }}>
          <button type="button" onClick={handleCancel}>
            <FaHome /> {t('common.home')}
          </button>
          <button
            type="button"
            onClick={() => setViewModeAndPersist(viewMode === 'grid' ? 'single' : 'grid')}
            title={viewMode === 'grid' ? 'Switch to single column' : 'Switch to grid (3 across)'}
          >
            {viewMode === 'grid' ? <FaList /> : <FaTh />} {viewMode === 'grid' ? 'List' : 'Grid'}
          </button>
          <button type="button" className="btn-primary-action" onClick={() => navigate(`/${tenant}/eventAdd`)}>
            <FaPlus /> {t('events.addEvent')}
          </button>
        </div>

        {/* Mobile view-mode toggle (sits parallel with header) */}
        <div className="eventlist-view-toggle" style={{ gridColumn: "1 / -1" }}>
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === 'single' ? 'active' : ''}`}
            onClick={() => setViewModeAndPersist('single')}
            aria-label="Single column"
          >
            <FaList />
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewModeAndPersist('grid')}
            aria-label="Grid 3 across"
          >
            <FaTh />
          </button>
        </div>
        {visibleEvents.length === 0 && (
          <div style={{ textAlign: "center", padding: "24px 0", color: "#a8a8a8", gridColumn: "1 / -1" }}>
            {searchTerm || dateFrom || dateTo
              ? "No events match your filters."
              : t('events.noEventsAvailable')}
          </div>
        )}
        {visibleEvents.map((evt) => {
          const media = pickMediaFromEvent(evt);
          const src = toAbs(baseUrl, media?.path || media?.url || media);
          const vid = isVideo(media);
          const img = isImage(media);
          const goToEvent = () => navigate(`/${tenant}/${evt.id}/eventDetail`);

          return (
            <article key={evt.id} className="feed-card feed-card--uniform">
              {/* header -> event details/add items */}
              <div
                className="feed-card__header feed-card__header--single"
                onClick={goToEvent}
                style={{ cursor: "pointer" }}
              >
                <div className="feed-card__header-text">
                  <div className="feed-card__title feed-card__title--single">
                    {evt?.name || `Event ${evt?.id}`}
                  </div>
                  {evt.ownership === "shared" && (
                    <span className="inline-block text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-200 px-2 py-0.5 rounded feed-card__shared-tag">
                      Shared by {evt.shared_by_name || "another tenant"} &middot; {evt.collab_role}
                    </span>
                  )}
                </div>
              </div>

              {/* edge-to-edge media — uniform aspect ratio */}
              <div className="feed-card__media-wrap" onClick={goToEvent} style={{ cursor: "pointer" }}>
                {vid ? (
                  <video
                    className="feed-media feed-media--uniform"
                    src={src}
                    controls
                    preload="metadata"
                    playsInline
                  />
                ) : (
                  <img
                    className="feed-media feed-media--uniform"
                    src={img ? src : toAbs(baseUrl, "/public/event.png")}
                    alt={evt?.name || "Event"}
                  />
                )}
              </div>

              {(evt?.description || evt?.location) && (
                <div className="feed-card__body feed-card__body--single">
                  {evt?.description && (
                    <div className="feed-card__desc--single" title={evt.description}>
                      {evt.description}
                    </div>
                  )}
                  {evt?.location && (
                    <div className="feed-card__sub feed-card__sub--single">{evt.location}</div>
                  )}
                </div>
              )}
            </article>
          );
        })}

        <AppFooter
          buttons={[
            {
              icon: <FaHome />,
              label: t('common.home'),
              onClick: handleCancel,
            },
            {
              icon: <FaPlus />,
              label: t('events.addEvent'),
              onClick: () => navigate(`/${tenant}/eventAdd`),
            },
          ]}
        />
      </div>
    </div>
  );
}